import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext.jsx'
import { useSession } from '../hooks/useSession.jsx'
import { useFactStream } from '../hooks/useFactStream.jsx'
import { ClaritySocket } from '../lib/ws.js'
import { AudioCapture, AUDIO_CONSTRAINTS } from '../lib/audio.js'
import { AudioBuffer } from '../lib/audioBuffer.js'
import { env } from '../config/env.js'
import RecordingBar from '../components/live/RecordingBar.jsx'
import SpineCard from '../components/live/SpineCard.jsx'
import SectionNav from '../components/live/SectionNav.jsx'
import FactStream from '../components/live/FactStream.jsx'
import MicDock from '../components/live/MicDock.jsx'
import ReadyToRecord from '../components/live/ReadyToRecord.jsx'
import ConsolidationAnimation from '../components/live/ConsolidationAnimation.jsx'

const KIND_LABELS = { decouverte: 'Découverte', bilan_annuel: 'Bilan annuel', suivi: 'Suivi', recommandation: 'Recommandation' }
const BACKOFFS = [1000, 2000, 5000, 15000, 15000, 15000]
const BUFFER_CAP = 200

function micErrorMessage(e) {
  switch (e?.name) {
    case 'NotAllowedError':
    case 'SecurityError':
      return "Accès au micro refusé. Autorisez le micro pour ce site (Réglages › Safari, ou l'icône « aA » dans la barre d'adresse), puis réessayez."
    case 'NotFoundError':
    case 'OverconstrainedError':
      return 'Aucun micro disponible sur cet appareil.'
    case 'NotReadableError':
      return 'Le micro est utilisé par une autre application. Fermez-la et réessayez.'
    default:
      return 'Micro inaccessible : ' + (e?.message || String(e))
  }
}

function deriveSpine(facts) {
  const spine = {}
  const peek = (section) => (facts[section] || []).find(f => f.is_essentiel)?.value
  spine.famille          = peek('bilan_personnel')
  spine.regime_tmi       = peek('situation_fiscale')
  spine.patrimoine_net   = peek('bilan_patrimonial')
  spine.profil_horizon   = peek('strategie_investissement')
  return spine
}

export default function LiveSessionPage() {
  const { id: routeId } = useParams()
  const nav = useNavigate()
  const { token, onUnauthorized, onQuotaExceeded, refreshUsage } = useAuth()
  const sess = useSession()
  const stream = useFactStream(sess.facts)

  const [elapsed, setElapsed] = useState(0)
  const [started, setStarted] = useState(false)
  const [micError, setMicError] = useState(null)
  const sockRef = useRef(null)
  const captureRef = useRef(null)
  const streamRef = useRef(null)
  const bufRef = useRef(null)
  const seqRef = useRef(0)
  const sidRef = useRef(null)
  // Recorded-time tracking: accumulator (ms of completed segments) +
  // segmentStart (Date.now() when the active segment began, or null when paused).
  // Elapsed = recordedMs + (segmentStart ? now - segmentStart : 0). This way
  // paused time is never counted, even across multiple pause/resume cycles.
  const recordedMsRef = useRef(0)
  const segmentStartRef = useRef(null)
  const retryRef = useRef(0)
  const reconnectTimerRef = useRef(null)
  const launchRef = useRef(null)
  const drainPendingRef = useRef(null)

  // Bail to the launcher if we landed on /pending with no payload. Runs on
  // mount regardless of whether the user has clicked Start yet.
  useEffect(() => {
    const launch = JSON.parse(sessionStorage.getItem('clarity_pending_launch') || 'null')
    if (!launch && routeId === 'pending') {
      nav('/sessions/new', { replace: true })
      return
    }
    launchRef.current = launch
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!started) return
    let cancelled = false
    const launch = launchRef.current
    if (!launch) return

    const wsBase = (env.WS_BASE_URL || `${location.protocol === 'https:' ? 'wss:' : 'ws:'}//${location.host}`) + '/ws/clarity/session'

    const wireSocket = (sock, { onReady } = {}) => {
      sock.on('started', (m) => {
        sidRef.current = m.session_id
        sock._sessionId = m.session_id
        sess.send({ type: 'STARTED', sessionId: m.session_id })
        try { sock.debug('started ack: sid=' + m.session_id) } catch { /* ignore */ }
        nav(`/sessions/live/${m.session_id}`, { replace: true })
        onReady?.(m.session_id)
      })
      sock.on('delta', (m) => sess.send({ type: 'DELTA', delta: m }))
      sock.on('final', (m) => {
        sess.send({ type: 'READY', extractedFacts: m.extracted_facts })
        refreshUsage?.()
        const sid = sidRef.current || routeId
        nav(`/sessions/${sid}`, { replace: true, state: { extractedFacts: m.extracted_facts } })
      })
      sock.on('error', (m) => sess.send({ type: 'ERROR', message: m.message }))
      sock.on('close', (e) => {
        // Auth failure on the WS path → log out, don't retry.
        if (e?.code === 1008 || e?.code === 4001) { onUnauthorized?.(); return }
        // Quota exceeded → flag context and route home; the SessionsList page opens the modal.
        if (e?.code === 4002) {
          onQuotaExceeded?.()
          nav('/', { replace: true })
          return
        }
        // Invalid client (P5b) → drop the stale launch payload and route back to the launcher.
        if (e?.code === 4003) {
          sessionStorage.removeItem('clarity_pending_launch')
          nav('/sessions/new', { replace: true })
          return
        }
        if (sess.state === 'recording' || sess.state === 'paused' || sess.state === 'degraded') {
          scheduleReconnect()
        }
      })
    }

    const scheduleReconnect = () => {
      if (cancelled) return
      const attempt = retryRef.current
      if (attempt === 0) sess.send({ type: 'WS_LOST' })
      if (attempt >= BACKOFFS.length) { sess.send({ type: 'WS_LOST_FINAL' }); return }
      const delay = BACKOFFS[attempt]
      retryRef.current = attempt + 1
      reconnectTimerRef.current = setTimeout(() => { void reconnect() }, delay)
    }

    const reconnect = async () => {
      if (cancelled) return
      try {
        const sock = new ClaritySocket(wsBase, token)
        sockRef.current = sock
        wireSocket(sock, {
          onReady: async (sid) => {
            // Successful reopen — replay anything we still had inflight.
            retryRef.current = 0
            const buf = bufRef.current
            if (buf) await buf.reopenInflight(sid)
            sess.send({ type: 'RESUME' })
          },
        })
        await sock.open()
        if (cancelled) return
        sock.start({
          kind: launchRef.current.kind,
          client_id: launchRef.current.client_id || '',
          consent_method: launchRef.current.consent_method,
          pre_seed_facts: launchRef.current.pre_seed_facts || null,
        })
      } catch {
        scheduleReconnect()
      }
    }

    const sock = new ClaritySocket(wsBase, token)
    sockRef.current = sock
    wireSocket(sock)

    sess.send({ type: 'START' })
    recordedMsRef.current = 0
    segmentStartRef.current = Date.now()

    let drainInterval, ackInterval

    ;(async () => {
      try {
        await sock.open()
        if (cancelled) return
        sock.start({
          kind: launch.kind,
          client_id: launch.client_id || '',
          consent_method: launch.consent_method,
          pre_seed_facts: launch.pre_seed_facts || null,
        })

        const buf = new AudioBuffer('clarity-audio')
        await buf.open()
        bufRef.current = buf

        const capture = new AudioCapture({
          onChunk: async (blob) => {
            const sid = sidRef.current
            if (!sid) { sockRef.current?.debug('onChunk DROP: no sid, size=' + blob.size); return }
            seqRef.current += 1
            const seq = seqRef.current
            try {
              // Store raw bytes, not the Blob: iOS Safari's IndexedDB silently
              // fails/hangs on Blob values, which dropped every chunk before the
              // drain loop could send it.
              const data = await blob.arrayBuffer()
              await buf.put({ sessionId: sid, seq, data })
              await buf.evictOldest(sid, BUFFER_CAP)
              sockRef.current?.debug('buffered seq=' + seq + ' bytes=' + data.byteLength)
            } catch (e) {
              sockRef.current?.debug('onChunk FAILED seq=' + seq + ': ' + (e?.message || e))
            }
          },
          onError: (err) => {
            // A recorder-level failure means audio is no longer being captured;
            // surface it instead of ending the session with an empty dossier.
            sess.send({ type: 'ERROR', message: micErrorMessage(err) })
          },
          // Breadcrumbs → server log, so the capture pipeline is traceable on
          // iOS Safari where the browser console isn't reachable.
          onDebug: (msg) => { try { sockRef.current?.debug(msg) } catch { /* ignore */ } },
        })
        captureRef.current = capture
        // Stream was acquired inside the Start tap (iOS gesture requirement).
        await capture.start(streamRef.current)

        // Drain loop: 500ms. Also exposed via drainPendingRef so onStop can
        // force-flush the in-progress cycle's blob before sending the WS
        // stop control (otherwise the partial chunk gets dropped).
        let drainNoSidLogged = 0
        const drainOnce = async () => {
          if (!sockRef.current?._raw || sockRef.current._raw.readyState !== 1) return
          const sid = sidRef.current
          if (!sid) {
            if (drainNoSidLogged++ < 3) sockRef.current.debug('drain skip: no sid yet')
            return
          }
          let next = await buf.takePending(sid)
          let sent = 0
          while (next) {
            sockRef.current.sendAudio(next.data)
            sent++
            next = await buf.takePending(sid)
          }
          if (sent > 0) sockRef.current.debug(`drain: sent ${sent} blob(s)`)
        }
        drainPendingRef.current = drainOnce
        drainInterval = setInterval(drainOnce, 500)

        // 30s ack tick.
        ackInterval = setInterval(() => {
          const sid = sidRef.current
          if (sid) buf.ackUpTo(sid, seqRef.current)
        }, 30000)
      } catch (e) {
        sess.send({ type: 'ERROR', message: e.message || String(e) })
      }
    })()

    return () => {
      cancelled = true
      if (drainInterval) clearInterval(drainInterval)
      if (ackInterval) clearInterval(ackInterval)
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current)
      sockRef.current?.close()
      captureRef.current?.stop()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [started])

  useEffect(() => {
    const i = setInterval(() => {
      const seg = segmentStartRef.current
      if (seg != null) {
        setElapsed(Math.floor((recordedMsRef.current + (Date.now() - seg)) / 1000))
      }
    }, 1000)
    return () => clearInterval(i)
  }, [])

  const launch = launchRef.current || JSON.parse(sessionStorage.getItem('clarity_pending_launch') || '{}')
  const counts = Object.fromEntries(Object.entries(sess.facts).map(([k, v]) => [k, v.length]))

  const closeSegment = () => {
    const seg = segmentStartRef.current
    if (seg != null) {
      recordedMsRef.current += Date.now() - seg
      segmentStartRef.current = null
      setElapsed(Math.floor(recordedMsRef.current / 1000))
    }
  }

  const onPauseToggle = () => {
    // Only meaningful in recording/paused. Block during degraded/stopping so
    // a stray click can't desync the timer accumulator from the WS state.
    if (sess.state !== 'recording' && sess.state !== 'paused') return
    if (sess.state === 'paused') {
      segmentStartRef.current = Date.now()
      captureRef.current?.resume()
      sockRef.current?.resume()
      sess.send({ type: 'RESUME' })
    } else {
      closeSegment()
      captureRef.current?.pause()
      sockRef.current?.pause()
      sess.send({ type: 'PAUSE' })
    }
  }

  const onStop = async () => {
    // Order matters here. The server's stop handler runs final consolidation
    // on whatever audio it has buffered at the moment it sees the WS stop
    // control, so we have to make sure the in-progress cycle's blob has
    // been flushed before we send it.
    //
    // 1. Freeze the timer at the final recorded value so the next render
    //    (the consolidation screen) doesn't briefly tick before unmounting.
    closeSegment()
    // 2. Tell the state machine + UI we're stopping (renders the animation).
    sess.send({ type: 'STOP' })
    // 3. Stop the mic AND wait for MediaRecorder.onstop, which writes the
    //    final blob to IndexedDB via the onChunk handler.
    try { await captureRef.current?.stop() } catch { /* swallow */ }
    // 4. Force the drain loop to send any pending blobs (including the one
    //    we just produced) over the WS.
    try { await drainPendingRef.current?.() } catch { /* swallow */ }
    // 5. Now signal end-of-audio to the server. It can safely flush + run
    //    Consolidate knowing nothing else is coming.
    sockRef.current?.stop()
  }

  const onCancelStart = () => {
    sessionStorage.removeItem('clarity_pending_launch')
    nav('/sessions/new', { replace: true })
  }

  // iOS Safari only grants getUserMedia inside the tap's transient activation,
  // which the WS + IndexedDB awaits in the recording effect would blow past.
  // So acquire the mic HERE, synchronously in the Start tap, and hand the live
  // stream to the capture pipeline. Without this, iOS captures zero audio.
  const handleStart = async () => {
    setMicError(null)
    try {
      streamRef.current = await navigator.mediaDevices.getUserMedia(AUDIO_CONSTRAINTS)
    } catch (e) {
      setMicError(micErrorMessage(e))
      return
    }
    setStarted(true)
  }

  const onRestart = async () => {
    const sid = sidRef.current
    if (sid && bufRef.current) { try { await bufRef.current.drop(sid) } catch { /* ignore */ } }
    nav('/sessions/new')
  }

  if (!started && sess.state === 'idle') {
    return (
      <ReadyToRecord
        clientName={launch.client_name || 'Client'}
        kindLabel={KIND_LABELS[launch.kind] || launch.kind}
        onStart={handleStart}
        onCancel={onCancelStart}
        error={micError}
      />
    )
  }
  if (sess.state === 'interrupted') {
    return (
      <div className="p-12 text-center">
        <h2 className="text-lg font-semibold">Session interrompue</h2>
        <p className="mt-2 text-muted">La connexion n'a pas pu être rétablie. L'audio enregistré a été supprimé.</p>
        <button onClick={onRestart} className="mt-6 rounded-lg bg-ink px-4 py-2 text-sm text-white">Recommencer</button>
      </div>
    )
  }
  if (sess.state === 'error') {
    const msg = typeof sess.error === 'string' && sess.error ? sess.error : 'Erreur inattendue'
    return (
      <div className="p-12 text-center">
        <h2 className="text-lg font-semibold text-terra-600">Erreur</h2>
        <p className="mt-2 text-[13px] text-muted">{msg}</p>
        <button onClick={onRestart} className="mt-6 rounded-lg bg-ink px-4 py-2 text-sm text-white">Retour</button>
      </div>
    )
  }
  if (sess.state === 'stopping' || sess.state === 'processing') {
    return <ConsolidationAnimation />
  }

  const spine = deriveSpine(sess.facts)
  const degraded = sess.state === 'degraded'

  return (
    <>
      {degraded && (
        <div className="bg-amber-100 px-4 py-2 text-center text-[12.5px] text-amber-900">
          Connexion perdue, reprise en cours…
        </div>
      )}
      <RecordingBar
        clientName={launch.client_name || 'Client'}
        kindLabel={KIND_LABELS[launch.kind] || launch.kind}
        elapsedSecs={elapsed}
        paused={sess.state === 'paused'}
        onPauseToggle={onPauseToggle}
        onStop={onStop}
      />
      <div className="grid grid-cols-1 md:grid-cols-[288px_1fr]">
        <aside className="hidden border-r border-line p-5 md:block">
          <div className="mb-5"><SpineCard spine={spine} /></div>
          <SectionNav counts={counts} activeKey={null} onSelect={() => {}} />
        </aside>
        <main className="px-4 py-4 pb-32 sm:px-7 sm:py-5 md:pb-5">
          <FactStream list={stream.list} filter={stream.filter} onFilterChange={stream.setFilter} />
        </main>
      </div>
      <div className="md:hidden">
        <MicDock elapsedSecs={elapsed} paused={sess.state === 'paused'} onPauseToggle={onPauseToggle} />
      </div>
    </>
  )
}
